<script lang="ts" setup>
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import Tooltip from "~/components/Common/Tooltip.vue";
import SvgButton from "~/components/Common/Buttons/SvgButton.vue";
import SvgIcon from "../SvgIcon.vue";

const importAction = ref<boolean>(false);
const tmp = ref<string | null | File>(null);
const emit = defineEmits(["update:modelValue"]);
const resize = ref(false);

const fileLogoInput = ref<HTMLInputElement | null>(null);

const props = defineProps({
  modelValue: {
    type: null,
  },
});

const removeLogo = () => {
  resetImage();
  if (fileLogoInput.value) {
    fileLogoInput.value.value = "";
  }
  importAction.value = false;

  tmp.value = null;
  emit("update:modelValue", null);
};

const handleFileDrop = (event: DragEvent) => {
  event.preventDefault();

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    const file = files[0];

    if (file.type.startsWith("image/")) {
      resetImage();

      const blob = URL.createObjectURL(file);
      logo.value.src = blob;
      logo.value.type = file.type;
    } else {
      console.warn("Veuillez déposer un fichier image.");
    }
  }
};

const resetImage = () => {
  logo.value = { src: null, type: null };
};
const triggerFileInputLogo = () => {
  resetImage();
  if (fileLogoInput.value) {
    fileLogoInput.value.value = "";
    fileLogoInput.value?.click();
  }
};

const triggerFileInputImport = () => {
  resetImage();
  importAction.value = true;
  if (fileLogoInput.value) {
    fileLogoInput.value.value = "";
    fileLogoInput.value?.click();
  }
};

onMounted(() => {
  nextTick(() => {
    tmp.value = props.modelValue;
  });
});

const initLoad = ref(true);

watch(
  () => props.modelValue,
  () => {
    if (initLoad.value) {
      tmp.value = props.modelValue;
      initLoad.value = false;
    }
  },
);

const cropperRef = ref();
const logo = ref<{ src: string | null; type: string | null }>({ src: null, type: null });
const uploadImage = (event: any) => {
  const { files } = event.target;
  if (files && files[0]) {
    if (logo.value.src) {
      resetImage();
    }
    const file = files[0];
    const fileUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      // Création d'un canvas fixe de 200x200
      const canvas = document.createElement("canvas");
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Remplir le canvas de blanc
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Calcul du facteur de scale pour garder le ratio et faire tenir l'image dans 200x200
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const newWidth = img.width * scale;
        const newHeight = img.height * scale;

        // Calcul des offsets pour centrer l'image dans le canvas
        const offsetX = (canvas.width - newWidth) / 2;
        const offsetY = (canvas.height - newHeight) / 2;

        // Dessiner l'image redimensionnée sur le canvas
        ctx.drawImage(img, offsetX, offsetY, newWidth, newHeight);

        // Récupérer le data URL et l'assigner comme source
        logo.value.src = canvas.toDataURL(file.type);
        logo.value.type = file.type;
      }
      // Libérer l'URL temporaire
      URL.revokeObjectURL(fileUrl);
    };
    img.src = fileUrl;
  }
};

const cropImage = () => {
  if (cropperRef.value) {
    const { canvas } = cropperRef.value.getResult();
    if (canvas) {
      canvas.toBlob((blob: any) => {
        if (blob) {
          const file = new File([blob], `cropped-image-${Date.now()}.png`, {
            type: "image/png",
            lastModified: Date.now(),
          });
          if (tmp.value) {
            URL.revokeObjectURL(tmp.value as string);
          }

          tmp.value = URL.createObjectURL(file);

          emit("update:modelValue", file);
          resetImage();
        }
      }, "image/png");
    }

    if (importAction.value) {
      importAction.value = false;
    }
  }
  resize.value = false;
};

const resetAfterDownload = () => {
  importAction.value = false;
  resize.value = false;
  resetImage();
};

const zoom = () => {
  if (cropperRef.value) {
    cropperRef.value.zoom(2);
  }
};

const dezoom = () => {
  if (cropperRef.value) {
    cropperRef.value.zoom(0.5);
  }
};

const adjust = async () => {
  await cropperRef.value.reset();

  nextTick(() => {
    cropperRef.value.zoom(0.5);
    resize.value = true;
  });
};

const transformFile = (file: File) => {
  return URL.createObjectURL(file);
};
</script>

<template>
  <div class="gap-[4px] flex flex-col w-full">
    <div
      v-if="(!logo.src && !tmp) || (importAction && !logo.src)"
      class="drop_area"
      @dragover.prevent
      @drop="handleFileDrop"
      @click="triggerFileInputLogo"
    >
      <div class="w-full h-full flex justify-center">
        <label for="file-input" class="flex gap-4 items-center">
          <img src="/icons/generic/upload.svg" :style="{ height: '64px', width: '64px' }" class="cursor-pointer" />
          <div class="upload-label">
            <span class="text-secondary-color w-full leading-7 flex flex-col">
              <span>Glissez-déposez ici le document à ajouter ou </span>
              <span class="text_underlined">Sélectionnez parmi vos fichiers logo</span>
              <span class="text-secondary-color">Formats de fichiers PDF, JPG, PNG - jusqu’à 10 Mo.</span>
            </span>
          </div>
          <input ref="fileLogoInput" type="file" accept="image/*" style="display: none" @change="uploadImage" />
        </label>
      </div>
    </div>
    <div v-if="logo.src" class="modal-backdrop">
      <div class="modal-content">
        <Cropper
          ref="cropperRef"
          :src="logo.src"
          :stencil-props="{
            handlers: {},
            movable: false,
            resizable: false,
            aspectRatio: 1,
            previewClass: 'preview',
          }"
          :default-size="{
            aspectRatio: 1,
            width: 200,
            height: 200,
          }"
          :resize-image="{
            adjustStencil: false,
          }"
          image-restriction="none"
        />
        <!-- Boutons de contrôle -->
        <div class="modal-buttons">
          <div class="vertical-buttons">
            <div class="square-button" @click="zoom()">
              <SvgIcon name="plus" class="w-4 h-4 flex items-center" />
            </div>
            <div class="square-button" @click="dezoom()">
              <SvgIcon name="checkbox-minus" class="w-4 h-4" />
            </div>
          </div>
          <div class="buttons-top">
            <div class="tag-button" @click="adjust()">Ajuster</div>
          </div>
          <div class="buttons-bottom">
            <div class="tag-button" @click="resetAfterDownload()">Annuler</div>
            <div class="tag-button" @click="cropImage()">Enregistrer</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="tmp && !importAction" class="flex flex-row items-center gap-8 mb-4">
      <div v-if="tmp" class="w-[98px] h-[98px] min-h-[98px]:">
        <img v-if="typeof tmp === 'string' && tmp?.startsWith('blob:')" :src="tmp" alt="Logo" class="h-full w-full" />
        <img
          v-else-if="typeof tmp === 'string' && !tmp?.startsWith('blob:')"
          :src="`${tmp}?cache=${Math.random()}`"
          alt="Logo"
          class="h-full w-full"
        />
        <img v-else alt="Logo" :src="transformFile(tmp as File)" class="h-full w-full" />
      </div>
      <div class="flex flex-col gap-2">
        <Tooltip :position="'top-mid'" append-to-body minified :force-position-top="5">
          <template #img>
            <label class="button_secondary_default flex gap-2 cursor-pointer">
              <SvgButton
                class="flex p-[2px] items-start"
                :style="{
                  height: '24px',
                  width: '24px',
                  'border-radius': '9px',
                }"
                name="upload-v2"
                @click="triggerFileInputImport"
              />
              Changer le logo
            </label>
          </template>
          <template #content>
            <div class="flex w-fit whitespace-nowrap">
              <span class="font-medium">Importer un nouveau logo</span>
            </div>
          </template>
        </Tooltip>

        <Tooltip :position="'top-mid'" append-to-body minified :force-position-top="5">
          <template #img>
            <label class="button_secondary_cancel_default flex gap-2 cursor-pointer">
              <SvgButton
                class="flex p-[2px] items-start primary-red-color"
                name="delete_trash"
                :default="false"
                :style="{
                  height: '24px',
                  width: '24px',
                  'border-radius': '9px',
                }"
                @click="removeLogo"
              />
              Supprimer le logo
            </label>
          </template>
          <template #content>
            <div class="flex w-fit whitespace-nowrap">
              <span class="font-medium">Supprimer le logo</span>
            </div>
          </template>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use "assets/scss/texts";

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  padding: 20px;
  border-radius: 8px;
  position: relative;
  width: 100%;
  max-width: 1000px;
  height: auto;
}

.vertical-buttons {
  position: absolute;
  left: 35px;
  top: 50%;
  transform: translateY(-50%);
}

.vertical-buttons-right {
  position: absolute;
  right: 0px;
  top: 10px;
  transform: translateX(-10px);
  display: flex;
  gap: 4px;
}

.buttons-top {
  position: absolute;
  right: 25px;
  top: 33px;
  transform: translateX(-10px);
  display: flex;
  gap: 4px;
}

.buttons-bottom {
  position: absolute;
  right: 25px;
  bottom: 33px;
  transform: translateX(-10px);
  display: flex;
  gap: 4px;
}

.square-button {
  background: var(--grey-lightest);
  color: var(--text-secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: background 0.5s;
  border-radius: 20px;

  &:hover {
    background: #ebe1dc;
  }
}

.tag-button {
  background: var(--grey-lightest);
  color: var(--text-secondary-color);
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 14px;
  cursor: pointer;
  display: inline-block;
  text-align: center;
}

.tag-button:hover {
  background-color: #ebdfd8;
  /* Couleur de fond survolée */
}
</style>

<style lang="scss" scoped>
@use "assets/scss/texts";

.drop_area {
  @apply flex flex-col items-center cursor-pointer h-[123px] w-full;
  transition: 0.2s ease;
  border-style: dashed;
  border-width: 2px;
  border-radius: 16px;

  &[data-active="true"] {
    background: var(--grey-light);
  }

  &:hover {
    cursor: pointer;
    background: var(--grey-light);
  }
}
.upload-label {
  @extend .body_regular;
  @apply flex flex-col items-center justify-center px-[16px] py-[28px] w-full h-full;
  cursor: pointer;

  span {
    @apply text-center;
    color: var(--text-secondary-color);
  }

  input[type="file"]:not(:focus-visible) {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  .text_underlined {
    text-decoration: var(--text-secondary-color) underline;
    text-decoration: underline;
    color: var(--primary-blue-color);
  }
}
</style>
